// ============================================================================
// Expediente documental — hooks de datos (react-query) sobre el servicio tipado.
// ============================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUserStore } from "../../store/userStore";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { selectedCompanyId } from "../../services/companyUseCase";
import {
  shipmentDocumentsService as svc,
  type ApplicableExtracted,
  type DocumentChecklist,
  type DocumentGenerator,
  type DocumentPatch,
  type DocumentSubjectRef,
  type DocumentTypesCatalog,
  type ShipmentDocument,
  type UploadInput,
} from "../../services/shipmentDocuments";

export const DOCS_QUERY_KEY = "shipmentDocuments";

/**
 * Compañía con la que se consulta el expediente: la del usuario con sesión y,
 * para un administrador de plataforma (sin `company_id`), la elegida en el
 * selector del encabezado (store de compañías o `localStorage.companyId`, como
 * EnviosLayout). Sin ella el API responde 400 `companyIdRequired`.
 */
export function useDocsCompanyId(): string | null {
  const user = useUserStore((s) => s.user as { company_id?: string | null } | null);
  const selected = useCompaniesStore((s) => s.companyId);
  return user?.company_id || selected || selectedCompanyId() || null;
}

/** Llave del checklist: lleva la compañía para refrescar al cambiarla en el selector. */
export const checklistKey = (subject: DocumentSubjectRef, companyId: string | null) => [DOCS_QUERY_KEY, "checklist", companyId, subject.kind, subject.id] as const;

/** Checklist de un envío o reserva; `enabled` para no consultar sin sujeto. */
export function useDocumentChecklist(subject: DocumentSubjectRef | null, options: { enabled?: boolean } = {}) {
  const companyId = useDocsCompanyId();
  return useQuery<DocumentChecklist>({
    queryKey: subject ? checklistKey(subject, companyId) : [DOCS_QUERY_KEY, "checklist", companyId, "none"],
    queryFn: () => svc.checklist(subject as DocumentSubjectRef, companyId),
    enabled: Boolean(subject?.id) && (options.enabled ?? true),
    staleTime: 30_000,
  });
}

/** Generadores de la plataforma para un sujeto (#1631). `data === null` = próximamente. */
export function useDocumentGenerators(subject: DocumentSubjectRef | null, enabled = true) {
  const companyId = useDocsCompanyId();
  return useQuery<DocumentGenerator[] | null>({
    queryKey: subject ? [DOCS_QUERY_KEY, "generators", companyId, subject.kind, subject.id] : [DOCS_QUERY_KEY, "generators", companyId, "none"],
    queryFn: () => svc.generators(subject as DocumentSubjectRef, companyId),
    enabled: enabled && Boolean(subject?.id),
    staleTime: 60_000,
    retry: false,
  });
}

/** Catálogo del API (tipos y `never_client_visible`); el checklist cae al espejo local si falla. */
export function useDocumentTypes() {
  return useQuery<DocumentTypesCatalog>({
    queryKey: [DOCS_QUERY_KEY, "types"],
    queryFn: () => svc.types(),
    staleTime: 30 * 60_000,
    retry: false,
  });
}

/** Acciones del checklist; cada una refresca el expediente y las vistas por compañía. */
export function useDocumentActions(subject: DocumentSubjectRef | null) {
  const qc = useQueryClient();
  const companyId = useDocsCompanyId();

  const patchCache = (doc: ShipmentDocument) => {
    if (!subject) return;
    qc.setQueryData<DocumentChecklist>(checklistKey(subject, companyId), (prev) => {
      if (!prev) return prev;
      const exists = prev.documents.some((d) => d.id === doc.id);
      return { ...prev, documents: exists ? prev.documents.map((d) => (d.id === doc.id ? doc : d)) : [...prev.documents, doc] };
    });
  };
  const invalidate = () => {
    if (subject) {
      void qc.invalidateQueries({ queryKey: checklistKey(subject, companyId) });
      void qc.invalidateQueries({ queryKey: [DOCS_QUERY_KEY, "generators", companyId, subject.kind, subject.id] });
    }
    void qc.invalidateQueries({ queryKey: [DOCS_QUERY_KEY, "company"] });
  };

  /** Relee un documento (p. ej. tras la lectura en segundo plano) y lo pone en el checklist. */
  const refresh = async (id: string) => {
    const doc = await svc.get(id);
    patchCache(doc);
    return doc;
  };

  /** Espera la lectura encolada al subir: relee 4 veces cada 5 s y va actualizando el checklist. */
  const pollExtraction = (id: string, opts: { times?: number; intervalMs?: number } = {}) => svc.pollExtraction(id, { ...opts, onTick: (doc) => patchCache(doc) });

  const materialize = useMutation({
    mutationFn: () => svc.materialize(subject as DocumentSubjectRef, companyId),
    onSuccess: (data) => {
      if (subject) qc.setQueryData(checklistKey(subject, companyId), data);
      invalidate();
    },
  });

  const upload = useMutation({
    mutationFn: ({ input, onProgress }: { input: Omit<UploadInput, "subject">; onProgress?: (pct: number) => void }) =>
      svc.upload({ ...input, subject: subject as DocumentSubjectRef }, onProgress),
    onSuccess: (result) => {
      patchCache(result.document);
      invalidate();
    },
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: DocumentPatch }) => svc.update(id, patch),
    onSuccess: (doc) => {
      patchCache(doc);
      invalidate();
    },
  });

  const generate = useMutation({
    mutationFn: ({ docType, options }: { docType: string; options?: Record<string, unknown> }) => svc.generate(subject as DocumentSubjectRef, docType, options),
    onSuccess: (result) => {
      patchCache(result.document);
      invalidate();
    },
  });

  const extract = useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) => svc.extract(id, { force }),
    onSuccess: (result) => patchCache(result.document),
  });

  const confirmExtracted = useMutation({
    mutationFn: ({ id, apply }: { id: string; apply: ApplicableExtracted[] }) => svc.confirmExtracted(id, apply),
    onSuccess: (doc) => {
      patchCache(doc);
      invalidate();
    },
  });

  return { materialize, upload, update, generate, extract, confirmExtracted, invalidate, refresh, pollExtraction };
}

/** Abre la URL firmada de una versión en una pestaña nueva. */
export async function openDownload(id: string, version?: number): Promise<void> {
  const info = await svc.downloadUrl(id, version);
  if (typeof window !== "undefined" && info?.url) window.open(info.url, "_blank", "noopener,noreferrer");
}
